import { JSONContent } from "novel";
import { AnalysisDocument } from "~/src/type/firebase/firestore";
import { GraphInfo } from "~/src/type/ui/article/article";
import { isValidUrl } from "~/src/lib/url";

/**
 * 既存の AnalysisDocument + GraphInfo[] を Tiptap JSONContent に変換する。
 * 既存データをリッチテキストエディタの初期コンテンツとして使用するための後方互換性対応。
 *
 * 全セクションが空の場合は undefined を返す。
 */
export function convertAnalysisToRichText(
  contents: AnalysisDocument,
  graphInfoList: GraphInfo[],
): JSONContent | undefined {
  const docContent: JSONContent[] = [];

  // 1. 詳細セクション
  if (contents.details) {
    docContent.push(
      createHeading("詳細"),
      ...splitTextToParagraphs(contents.details),
    );
  }

  // 2. 前提セクション
  if (contents.premise) {
    docContent.push(
      createHeading("前提"),
      ...splitTextToParagraphs(contents.premise),
    );
  }

  // 3. グラフセクション
  if (contents.graphDescription || graphInfoList.length > 0) {
    docContent.push(createHeading("グラフ"));

    if (contents.graphDescription) {
      docContent.push(...splitTextToParagraphs(contents.graphDescription));
    }

    // indexでソートしてgraphBlockノードを挿入
    const sortedGraphs = [...graphInfoList].sort(
      (a, b) => (a.info.index ?? 0) - (b.info.index ?? 0),
    );
    for (const g of sortedGraphs) {
      docContent.push({
        type: "graphBlock",
        attrs: { graphId: g.id },
      });
    }
  }

  // 4. 結論セクション
  if (contents.conclusion) {
    docContent.push(
      createHeading("結論"),
      ...splitTextToParagraphs(contents.conclusion),
    );
  }

  // 5. 参考セクション
  if (contents.reference && contents.reference.length > 0 && contents.reference.some((r) => r.length > 0)) {
    docContent.push(createHeading("参考"));

    for (const ref of contents.reference) {
      if (!ref) continue;

      if (isValidUrl(ref)) {
        // URL形式の場合はリンクカード
        docContent.push({ type: "linkCard", attrs: { url: ref } });
      } else {
        // URL形式でない場合は引用
        docContent.push({
          type: "blockquote",
          content: [{ type: "paragraph", content: [{ type: "text", text: ref }] }],
        });
      }
    }
  }

  // 何も変換するデータがない場合
  if (docContent.length === 0) return undefined;

  return { type: "doc", content: docContent };
}

/**
 * 見出し1（h2）ノードを生成
 */
function createHeading(text: string): JSONContent {
  return {
    type: "heading",
    attrs: { level: 2 },
    content: [{ type: "text", text }],
  };
}

/**
 * テキストを改行で分割してparagraphノード配列を生成
 */
function splitTextToParagraphs(text: string): JSONContent[] {
  return text.split("\n").map((line) =>
    line.length > 0
      ? { type: "paragraph", content: [{ type: "text", text: line }] }
      : { type: "paragraph" },
  );
}
